/**
 * react-phone-input-2's country-code dropdown (`.country-list`) is forced to
 * `position: fixed` inside antd modals (see antd.css's
 * `.ant-modal-wrap .react-tel-input .country-list` rule) so it isn't clipped
 * by the modal body's `overflow-y: auto` — but the library itself never
 * computes real viewport coordinates for it, it only relies on the browser's
 * default "static position" fallback for a `fixed` element. That fallback
 * does not account for a scrolled modal body sitting between the dropdown
 * and the viewport, so opening the dropdown after the modal has already been
 * scrolled renders it detached from the flag button, floating over unrelated
 * content instead of tucked underneath it.
 *
 * `.country-list` is only added to the DOM while open (react-phone-input-2
 * conditionally renders it via a `showDropdown` state flag) and removed on
 * close, so watch for that instead of hooking into the library, which
 * exposes no open/close callback. Same rationale/pattern as
 * closeOverlaysOnScroll: fixed once here rather than rewired into every
 * PhoneInput call site across the app.
 */

const positionDropdown = (list) => {
  const flagDropdown = list.closest(".flag-dropdown");
  if (!flagDropdown) return;
  const rect = flagDropdown.getBoundingClientRect();
  list.style.position = "fixed";
  list.style.top = `${rect.bottom}px`;
  list.style.left = `${rect.left}px`;
  list.style.margin = "0";
};

/** Re-run positioning for whichever `.country-list` is currently open. */
const repositionOpenDropdown = () => {
  document.querySelectorAll(".react-tel-input .country-list").forEach(positionDropdown);
};

const handleMutations = (mutations) => {
  for (const mutation of mutations) {
    mutation.addedNodes.forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      if (node.matches?.(".country-list")) {
        positionDropdown(node);
      } else {
        node.querySelectorAll?.(".country-list").forEach(positionDropdown);
      }
    });
  }
};

/**
 * Start watching. Safe to call once at app start; stays attached for the
 * app's lifetime, same pattern as closeOverlaysOnScroll/closeOverlaysOnModalOpen.
 *
 * @returns {Function} unsubscribe
 */
const fixPhoneDropdownPosition = () => {
  const observer = new MutationObserver(handleMutations);
  observer.observe(document.body, { childList: true, subtree: true });

  // The dropdown is genuinely `position: fixed` once positioned, so it does
  // track a page-level scroll on its own — but not the modal body's own
  // scroll (a different, inner scroll container), which would otherwise
  // leave it floating over the wrong spot if the modal is scrolled while
  // the dropdown is still open.
  window.addEventListener("scroll", repositionOpenDropdown, true);

  return () => {
    observer.disconnect();
    window.removeEventListener("scroll", repositionOpenDropdown, true);
  };
};

export default fixPhoneDropdownPosition;
